import { useState } from "react";
import { Flag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { REPORT_CATEGORIES, uploadEvidence } from "@/lib/trust";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function ReportButton({ targetType, targetId, ownerId, label = "Report", className = "" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("other");
  const [details, setDetails] = useState("");
  const [files, setFiles] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!user || !targetId || (ownerId && ownerId === user.user_id)) return null;
  const submit = async () => {
    setBusy(true);
    try {
      const evidence_file_ids = await uploadEvidence(files);
      await api.post("/reports", { target_type: targetType, target_id: targetId, category, details, evidence_file_ids });
      toast.success("Report received. Moderators will review it; you can follow it under My moderation.");
      setOpen(false); setDetails(""); setFiles(null);
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  return (
    <>
      <button type="button" className={`btn-ghost text-xs text-slate-400 hover:text-red-300 ${className}`} onClick={() => setOpen(true)} data-testid={`report-button-${targetType}`}>
        <Flag className="h-3.5 w-3.5" />{label}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#2A3447] bg-[#121620] text-slate-200" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-100">Report this {targetType.replace("_", " ")}</DialogTitle>
            <DialogDescription className="text-slate-400">Reports are private. The person you report never sees who filed it. Evidence images are visible only to authorised moderators.</DialogDescription>
          </DialogHeader>
          <label className="field-label" htmlFor="report-category">Category</label>
          <select id="report-category" className="field" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="report-category-select">
            {REPORT_CATEGORIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <label className="field-label" htmlFor="report-details">What happened? (at least 10 characters)</label>
          <textarea id="report-details" rows={4} className="field" value={details} onChange={(e) => setDetails(e.target.value)} data-testid="report-details-input" />
          <label className="field-label" htmlFor="report-evidence">Evidence screenshots (optional, up to 5 PNG/JPG/WebP)</label>
          <input id="report-evidence" type="file" multiple accept="image/png,image/jpeg,image/webp" onChange={(e) => setFiles(e.target.files)} className="text-xs" data-testid="report-evidence-input" />
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setOpen(false)} data-testid="report-cancel-button">Cancel</button>
            <button className="btn-gold" disabled={busy || details.trim().length < 10} onClick={submit} data-testid="report-submit-button">{busy ? "Sending…" : "Submit report"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
